import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Autocomplete,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Mail } from 'lucide-mui';
import { shareThunk } from '../../selectedVideoSlice';
import { getValidEmail } from '../../../../lib/textUtils';
import type { GlobalDialogProps } from '../../../../types';
import { RootState } from '../../../../reducers';
import { setAlertMessage } from '../../../dashboard/dashboardSlice';
import { sendEmailThunk } from '../../../app/appSlice';
import { getShareUrl } from '../../../../lib/routingUtils';
import emailTemplates from '../../../email/templates';

export const ShareByEmailModal = ({ onClose }: GlobalDialogProps) => {
  const { selectedVideo, settings } = useSelector((state: RootState) => state);
  const { file } = selectedVideo;
  const fullName = settings?.user?.name || 'Someone';
  const firstName = fullName.split(' ')[0];
  const [emails, setEmails] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [invalidEmailError, setInvalidEmailError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '28px',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      minHeight: '48px',
      padding: '4px 8px',
      '& fieldset': {
        border: 'none',
      },
      '& .MuiInputAdornment-root': {
        marginRight: '-6px',
        height: '100%',
      },
      '& .MuiChip-root': {
        borderRadius: '16px',
        height: '32px',
        margin: '4px',
        fontSize: '0.9rem',
      },
      '& .MuiAutocomplete-endAdornment': {
        right: '14px',
      },
    },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      padding: '12px 16px',
      fontSize: '1rem',
      lineHeight: '1.5',
    },
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const validateAndUpdateEmails = (emailList: string[]) => {
    const invalidEmails = emailList.filter((email) => !getValidEmail(email));
    if (invalidEmails.length) {
      setInvalidEmailError(
        `Please remove invalid emails: ${invalidEmails.join(', ')}`
      );
      return false;
    }
    setInvalidEmailError('');
    setEmails(emailList);
    return true;
  };

  const handleEmailChange = (
    event: React.SyntheticEvent | null,
    newEmails: string[]
  ) => {
    validateAndUpdateEmails(newEmails);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setCurrentInput(input);
  };

  const handleShare = async () => {
    // If there's a valid email in the input, add it to the list
    const allEmails = currentInput ? [...emails, currentInput] : emails;

    if (!validateAndUpdateEmails(allEmails)) {
      return;
    }

    try {
      setIsLoading(true);
      // First generate/get the share link
      const shareAction = await dispatch(
        shareThunk({
          fileId: file._id,
          copyUrlToClipboard: false,
        })
      );

      // @ts-ignore - we know the structure of the fulfilled action payload
      const shareId = shareAction.payload?.shareId;
      if (!shareId) {
        throw new Error('Failed to generate share link');
      }

      const shareUrl = getShareUrl(shareId);

      // Send emails in parallel
      await Promise.all(
        allEmails.map((email) =>
          dispatch(
            sendEmailThunk({
              template: 'genericEmail',
              toEmail: email,
              data: emailTemplates.shareVideo({
                senderName: firstName,
                fileName: file.name,
                shareUrl,
              }),
            })
          )
        )
      );

      dispatch(
        setAlertMessage({
          message: `Video shared with ${allEmails.length} recipient${
            allEmails.length === 1 ? '' : 's'
          }!`,
          type: 'success',
          buttonText: 'Dismiss',
        })
      );
      handleClose();
    } catch (error) {
      console.error('Share error:', error);
      dispatch(
        setAlertMessage({
          message: 'Failed to share video. Please try again.',
          type: 'error',
          buttonText: 'Dismiss',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasValidInput = currentInput ? !!getValidEmail(currentInput) : false;
  const canShare =
    (emails.length > 0 || hasValidInput) && !invalidEmailError && !isLoading;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          pt: 3,
          pb: 2,
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        Share via email
      </DialogTitle>
      <DialogContent
        sx={{
          px: 4,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box
          sx={{
            color: 'text.secondary',
            typography: 'body1',
          }}
        >
          Share &ldquo;{file.name}&rdquo; with others by entering their email
          addresses below. They&apos;ll receive a secure link to access the
          content.
        </Box>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={emails}
          onChange={handleEmailChange}
          disabled={isLoading}
          inputValue={currentInput}
          onInputChange={(event, value) => setCurrentInput(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Enter email addresses"
              variant="outlined"
              error={!!invalidEmailError}
              helperText={invalidEmailError || 'Type or paste email addresses'}
              onChange={handleInputChange}
              sx={inputStyles}
            />
          )}
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              padding: '4px 8px',
              minHeight: '56px',
            },
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            minWidth: '100px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          startIcon={<Mail />}
          disabled={!canShare}
          color="primary"
          variant="contained"
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            minWidth: '100px',
          }}
        >
          {isLoading ? 'Sharing...' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareByEmailModal;
